// The detailed tree - every element of the model with its amounts.
//
// Mapped the way the timeline viewer maps a record file: the record's
// category, then its group (the family / type the exporter grouped the
// elements under), then the elements. Between Level and Status sit the
// columns tracking and procurement read: count and quantity, length,
// area, surface area, volume and weight rolled up from each element to
// its group and category, and on each element its own width, depth,
// height, diameter and thickness.
//
// Loaded a level at a time from the server (utils/elementQuantities): a
// group can hold a hundred thousand elements, and they arrive in pages
// of 500 when it is opened.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Dropdown, Empty, Input, Progress, Space, Spin, Table, Tag, Tooltip, message } from "antd";
import { popupInStage } from "./stagePopup";
import { modelNameFor, modelNameTooltip } from "./bothNames";
import { DownOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { getQuantityTree, rebuildQuantities, setElementStatus } from "api/tracking/trackingElementsApi";
import { STATUS_COLOR, STATUS_LABEL, STATUS_ORDER } from "./trackingTheme";
import { dateForStatus } from "./stageHelpers";
import { announceStatus, setStatusWithPreview } from "./statusBulk";
import { CERTAINTY, DEFAULT_UNITS, DETAIL_DIMENSIONS, TABLE_SUMS, buildNote, certaintyMark, certaintyNote, certaintyOf, certaintyTitle, compact, exact, unitOf } from "./quantityFormat";
import { checkedBy, excludedBy, toggleExclusion, toggleRecordKey } from "./recordKeys";
import { EMPTY as NO_FILTERS, anyActive, forServer, fromSaved, pickFilter, rangeFilter, rulesInWords, textFilter } from "./quantityHeaderFilters";
import RuleBuilder from "./RuleBuilder";

const T = { muted: "#6B7280", ink: "#111827", line: "#E5E7EB" };
const PAGE = 500;
const placeholder = (parentKey) => [{ key: `${parentKey}::loading`, kind: "loading" }];

// The tick keys a category row stands for - the same keys its checkbox would
// tick, so isolating and ticking can never mean two different sets.
function isolateKeysFor(row) {
  const r = row.realKey ? { ...row, key: row.realKey, kind: row.kind === "shortcut" ? "category" : row.kind } : row;
  return toggleRecordKey([], r, true);
}
const canIsolateRow = (row) => row.kind === "category" || (row.kind === "shortcut" && !!row.realKey);

export default function QuantityTree({
  projectId, documentIds = [], canEdit = false, statusByUid, statusDate = "", projectedDate = "",
  canSaveRules = true,
  onStatusChanged, onSelectElement, height = 520, scopeStamp = "",
  // Bumped by the page after a status is written ANYWHERE else - the
  // shared bulk bar, the Model elements tree, the model itself - so the
  // numbers here follow without a reload (owner, 2026-09-21: "the
  // filtered tracking quantities page to reflect the new state in real
  // time"). Writes made from this tree refresh it themselves.
  refreshKey = 0,
  // THE CHOSEN SET (owner, 2026-09-22: "the tracking quantities table
  // should pull from the set created from Model elements"). While one is
  // chosen the table lists only its elements - it travels in the SAME
  // filter object, so the rows, the rule dropdowns, select-all, the
  // preview and the write all narrow by it on the server.
  listId = "", listName = "",
  // The page's levels (owner, 2026-09-22): part of the same filter, so the
  // table, its rules, select-all and the write all stay on them.
  pageLevels = [],
  // The elevation half of the page cut (utils/pageCut): a selection id,
  // travelling with the levels so the table counts what the model draws.
  cutUids = "",
  // A BOX drawn in the model (models/trackingSelection): the table shows
  // only what it caught until it is cleared.
  // Kept in the signature and deliberately UNUSED for filtering: the
  // page still passes them, and the Selected tab beside this one is what
  // consumes a selection now. Removing them from the props would only
  // move the change into the caller.
  selectionId = "", selectionCount = 0, onClearSelection = null,
  // The ticks live in the page, not here: they are the page's selection -
  // what the viewer highlights, isolates and what the bulk bar acts on -
  // exactly as the Model elements tree's ticks are (owner via the leader,
  // 2026-09-20: the two trees must read the same way). `bulkBar` is that
  // one shared bar, handed down rather than rebuilt.
  checked = [], onChecked = null, bulkBar = null,
  // ISOLATE A WHOLE CATEGORY (owner, 2026-10-26: "select a full category and
  // isolate for status update"). The page owns what it means; this tree only
  // offers the button on category rows and says which one is isolated.
  onIsolate = null, isolatedStamp = "",
  // What was dropped out of a ticked branch: "this category except that
  // one" (owner, 2026-09-20).
  excluded = [], onExcluded = null,
  // WHAT IS SELECTED ELSEWHERE, so this tree shows it too (owner,
  // 2026-09-20: "all 3 panels are 2 way selection edit portals"). These
  // are NOT this tree's ticks and they change nothing about them: a level
  // picked in Selection details, or an element clicked in the model,
  // marks the rows it lands on here so a person can see where their
  // selection IS rather than being told a number for it.
  //
  // It marks what it can see without asking the server anything: element
  // rows by their uid, and BOTH parent kinds by the levels they carry.
  // The category row was unmarked until 2026-09-20, on my reading that
  // the roll-up did not hold levels for it - it does, per group row, and
  // the category listing simply never asked. Reading the query told me
  // about the data and was wrong; the levels now come back with the
  // category for 21 ms and no migration.
  selectedUids = null,
  selectedLevels = [],
  // SELECT EVERYTHING THE FILTER MATCHED (owner, 2026-09-21: "after i
  // filtered by a value, i need to be able to select everything filtered
  // - a check box on top of all checkboxes ... before the header name").
  // The page owns it, because the count it produces is the count the
  // bulk bar shows and the set the status write touches - one number,
  // one resolution, server-side.
  onFiltersChange = null,
  // WHAT THE PAGE REMEMBERED, and a key that changes when the PAGE does.
  // The key is what makes this a restore rather than a controlled prop: the
  // headers stay the owner of the filter while a page is open, and only a
  // page change re-seats them. Without the key, every parent re-render would
  // fight whatever the person had just ticked.
  savedFilters = null,
  savedFiltersKey = "",

  allFiltered = false,
  onAllFiltered = null,
  allFilteredCount = null,
}) {
  const [rows, setRows] = useState([]);
  const [quantities, setQuantities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const [filter, setFilter] = useState("");
  // THE HEADER FILTERS (owner, 2026-09-20: "filter by all headers at all
  // levels of the tree"). They are answered by the SERVER, which rebuilds
  // the tree from the elements that matched - so a category is here only
  // if something under it matched, and its count and sums are the filtered
  // ones. The text box above the table is a different thing and says so:
  // it hides rows ALREADY LOADED, without asking anybody.
  const [headerFilters, setHeaderFilters] = useState(() => fromSaved(savedFilters));
  // SEAT THE PAGE'S FILTER WHEN THE PAGE CHANGES, and never in between.
  // fromSaved fills the whole shape, so a saved filter that omitted `ranges`
  // or `rules` - forServer drops both when empty - cannot leave a hole for
  // every reader below to guard against.
  const seatedKey = useRef(savedFiltersKey);
  useEffect(() => {
    if (seatedKey.current === savedFiltersKey) return;
    seatedKey.current = savedFiltersKey;
    setHeaderFilters(fromSaved(savedFilters));
  }, [savedFiltersKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [rulesOpen, setRulesOpen] = useState(false);
  const [levelChoices, setLevelChoices] = useState([]);

  const pageLevelsKey = pageLevels.join("|");
  // SELECTION DOES NOT NARROW THIS TREE (owner, 2026-09-24: tracking
  // quantities shows the rule-filtered elements ONLY and is never
  // affected by selection). A box drawn in the model used to be folded
  // in here as `selectionId`, so drawing one silently changed every
  // count and amount on this screen - the tree answered "what is in the
  // box" while its heading said "what the rules cover".
  //
  // IT STILL MARKS. His other sentence - the highlight must follow the
  // selection in real time - is only compatible with this one if
  // selection decides which rows are LIT and never which rows EXIST.
  // `selectedUids` does the marking and was never part of the filter;
  // `selectionId` was the filter, and it is gone. What is selected has
  // its own tab now, beside this one.
  const serverFilters = useMemo(() => {
    const base = forServer(headerFilters);
    // The CUT is not part of the filter object: it rides as its own query
    // param and the server resolves it (a selection id, not 5,557 uids).
    // Including it here would both leak a uid list into the filter string
    // - which the server now drops - and make an unfiltered page read as
    // filtered.
    if (!listId && !pageLevels.length) return base;
    return {
      ...(base || { name: "", levels: [], statuses: [], ranges: {} }),
      ...(listId ? { listId } : {}),
      ...(pageLevels.length ? { pageLevels } : {}),
    };
  }, [headerFilters, listId, pageLevelsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Filtering = a header filter, a rule, or the set. NOT the selection,
  // and NOT the page's cut: the cut WIDENS what a level filter admits
  // rather than narrowing anything, so a page with the untagged switch on
  // and no filter is not a filtered page. The server's own active() says
  // the same, and the two disagreeing is how a chip ends up lying.
  const filtering = anyActive(headerFilters) || !!listId || pageLevels.length > 0;
  const filterStamp = useMemo(() => (serverFilters ? JSON.stringify(serverFilters) : ""), [serverFilters]);
  const setOne = useCallback((patch) => setHeaderFilters((prev) => ({ ...prev, ...patch })), []);
  // The page needs the ACTIVE filter, not the draft: it is what the
  // selection carries and what the server re-resolves at write time.
  //
  // AND IT SAYS WHICH PAGE THE FILTER IS FOR. Without that the page could not
  // tell "the person edited this page's filter" from "this filter belongs to
  // the page we just left", and it saved the OLD page's filter onto the NEW
  // one: SpaceX Page 1 (cut Level 1) ended up storing levels:["Level 2"]
  // a minute after Page 2 did, so both pages filtered to Level 2 and showed
  // the same quantities (owner, 2026-09-29: "page 1 levl1 is showing same
  // quantities in page2"). The stamp travels WITH the value; a consumer that
  // compares it to its own active page cannot be raced by an effect ordering.
  useEffect(() => {
    if (onFiltersChange) onFiltersChange(serverFilters, savedFiltersKey);
  }, [filterStamp]); // eslint-disable-line react-hooks/exhaustive-deps
  const setRange = useCallback((key, r) => setHeaderFilters((prev) => ({ ...prev, ranges: { ...prev.ranges, [key]: r } })), []);
  const [busy, setBusy] = useState("");
  const [scoped, setScoped] = useState(0);
  const docs = useMemo(() => documentIds.join(","), [documentIds]);
  const units = (quantities && quantities.units) || DEFAULT_UNITS;

  // THE LATEST ASK WINS. Two filters run in quick succession used to race:
  // a slow first answer landing last put the OLD filter's rows under the
  // new filter's banner. A superseded answer is dropped, and the spinner
  // stays up until the answer for what is on screen arrives.
  const rootSeq = useRef(0);
  const loadRoot = useCallback(async () => {
    if (!projectId) return;
    const mine = (rootSeq.current += 1);
    setLoading(true);
    try {
      const r = await getQuantityTree(projectId, {
        documentIds: docs, scope: 1,
        // THE CUT, AS ITS OWN PARAM. Without this the route resolves
        // nothing and the header filter throws out the untagged rows the
        // switch admitted - which is what it did until 2026-10-02.
        ...(cutUids ? { cutUids } : {}),
        ...(filterStamp ? { filters: filterStamp } : {}),
      });
      if (rootSeq.current !== mine) return;
      setQuantities(r.quantities || null);
      setScoped(r.scoped ? r.scopeKeys : 0);
      // The Level menu offers what this model HAS. Sent with the root
      // listing only, and kept when a filtered listing omits it - a menu
      // that shrank to the levels that survived the last filter could
      // never be widened again.
      if (Array.isArray(r.levels)) setLevelChoices(r.levels);
      setRows((r.nodes || []).map((n) => ({ ...n, children: n.groups ? placeholder(n.key) : undefined })));
      setExpanded([]);
    } catch (e) {
      if (rootSeq.current === mine) message.error(e.message || "Could not read the quantities");
    } finally {
      if (rootSeq.current === mine) setLoading(false);
    }
  }, [projectId, docs, scopeStamp, filterStamp]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadRoot(); }, [loadRoot]);

  // Until the build is done, look again every 15 s.
  useEffect(() => {
    if (!quantities || ["done", "none", "failed"].includes(quantities.state)) return undefined;
    const t = setTimeout(loadRoot, 15000);
    return () => clearTimeout(t);
  }, [quantities, loadRoot]);

  const replaceChildren = (key, children) => setRows((prev) => {
    const walk = (list) => list.map((r) => (r.key === key ? { ...r, children } : (Array.isArray(r.children) ? { ...r, children: walk(r.children) } : r)));
    return walk(prev);
  });

  // One page of a group's elements, as the children rows the table shows.
  const elementsPage = useCallback(async (group, skip = 0, have = [], limit = PAGE) => {
    const r = await getQuantityTree(projectId, {
      documentIds: docs, scope: 1, category: group.category, skip, limit,
      // A SIZE NODE of Conduits asks by size (back/utils/conduitSizes).
      ...(group.sized ? { material: group.material, size: group.size } : { group: group.group }),
      // The drill-down needs it too, or a group opens and loses the rows
      // its own parent row counted.
      ...(cutUids ? { cutUids } : {}),
      ...(filterStamp ? { filters: filterStamp } : {}),
    });
    // An element row names the COMMIT's category/family/type; which record
    // group it was loaded from is what a tick above it means, so stamp it.
    // UNDER A SHORTCUT (Conduits by size, Junction boxes, Pull boxes) the
    // same element can be open in its real place too, so its row key is
    // prefixed and its real key travels as realKey.
    const elements = (r.nodes || []).map((e) => ({
      ...e, recordCategory: group.category, recordGroup: group.group,
      ...(group.sized ? { recordSizeKey: group.realKey || group.key } : {}),
      ...(group.realKey ? { key: `${group.key}::${e.key}`, realKey: e.key } : {}),
    }));
    const all = [...have, ...elements];
    const more = (r.total || 0) > all.length
      ? [{ key: `${group.key}::more:${all.length}`, kind: "more", group, skip: all.length, have: all, total: r.total }]
      : [];
    return [...all, ...more];
    // cutUids IS IN HERE because it is READ in here. Without it a
    // drill-down built before the switch was turned on keeps sending the
    // old value - a stale closure, which on this page means a group that
    // opens and loses the rows its own parent row counted.
  }, [projectId, docs, filterStamp, cutUids]);
  const loadElements = useCallback(async (group, skip = 0, have = []) => {
    replaceChildren(group.key, await elementsPage(group, skip, have));
  }, [elementsPage]);

  const onExpand = useCallback(async (open, record) => {
    const loaded = Array.isArray(record.children) && !(record.children.length === 1 && record.children[0].kind === "loading");
    if (!open || loaded) return;
    try {
      if (record.kind === "category") {
        const r = await getQuantityTree(projectId, { documentIds: docs, scope: 1, category: record.category, ...(filterStamp ? { filters: filterStamp } : {}) });
        replaceChildren(record.key, (r.nodes || []).map((g) => ({ ...g, children: placeholder(g.key) })));
      } else if (record.kind === "shortcut") {
        const r = await getQuantityTree(projectId, { documentIds: docs, scope: 1, shortcut: record.shortcut, ...(cutUids ? { cutUids } : {}), ...(filterStamp ? { filters: filterStamp } : {}) });
        // Cord reels list their elements directly: leaves, nothing to load.
        replaceChildren(record.key, (r.nodes || []).map((g) => (g.kind === "element" ? g : { ...g, children: placeholder(g.key) })));
      } else if (record.kind === "group") {
        await loadElements(record);
      }
    } catch (e) {
      message.error(e.message || "Could not open that row");
    }
  }, [projectId, docs, loadElements, filterStamp]);

  // THE ROWS THAT ARE OPEN, READ AGAIN AFTER A CHANGE - UNDER THE FILTER
  // THAT IS ON. This used to re-read the tree WITHOUT the filter and merge
  // the unfiltered numbers into the filtered rows, and it never dropped a
  // row: filter by "Not started", mark them Installed, and the table kept
  // showing them, with the whole model's counts. Now the open structure is
  // rebuilt from the filtered answer - categories, the groups of every
  // opened category, and the elements of every opened group - so a row
  // that stopped matching goes, and one that started matching arrives.
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const refreshLoaded = useCallback(async () => {
    const withF = filterStamp ? { filters: filterStamp } : {};
    const now = rowsRef.current;
    const top = await getQuantityTree(projectId, { documentIds: docs, scope: 1, ...withF });
    const topKeys = new Set((top.nodes || []).map((n) => n.key));
    const opened = now.filter((r) => (r.kind === "category" || r.kind === "shortcut") && topKeys.has(r.key)
      && Array.isArray(r.children) && r.children.some((c) => c.kind === "group"));
    const groupsOf = new Map();
    await Promise.all(opened.map((c) => getQuantityTree(projectId, { documentIds: docs, scope: 1, ...(c.kind === "shortcut" ? { shortcut: c.shortcut } : { category: c.category }), ...withF })
      .then((g) => groupsOf.set(c.key, g.nodes || [])).catch(() => {})));
    // Groups whose elements were loaded, and are still there to hold them.
    const elementsOf = new Map();
    const loadedGroups = [];
    opened.forEach((c) => {
      const still = new Set((groupsOf.get(c.key) || []).map((g) => g.key));
      c.children.forEach((g) => {
        if (g.kind !== "group" || !still.has(g.key) || !Array.isArray(g.children)) return;
        const shown = g.children.filter((x) => x.kind !== "loading" && x.kind !== "more").length;
        if (shown) loadedGroups.push({ g, limit: Math.max(PAGE, shown) });
      });
    });
    await Promise.all(loadedGroups.map(({ g, limit }) => elementsPage(g, 0, [], limit)
      .then((kids) => elementsOf.set(g.key, kids)).catch(() => {})));
    if (top.quantities) setQuantities(top.quantities);
    setRows((prev) => {
      const oldBy = new Map(prev.map((r) => [r.key, r]));
      return (top.nodes || []).map((n) => {
        const old = oldBy.get(n.key);
        if (!groupsOf.has(n.key)) return { ...n, children: n.groups ? ((old && old.children) || placeholder(n.key)) : undefined };
        const oldGroups = new Map(((old && old.children) || []).map((g) => [g.key, g]));
        return {
          ...n,
          children: groupsOf.get(n.key).map((g) => {
            if (g.kind === "element") return g;
            if (elementsOf.has(g.key)) return { ...g, children: elementsOf.get(g.key) };
            const og = oldGroups.get(g.key);
            const openedBefore = og && Array.isArray(og.children) && og.children.some((x) => x.kind !== "loading");
            return { ...g, children: openedBefore ? og.children : placeholder(g.key) };
          }),
        };
      });
    });
  }, [projectId, docs, filterStamp, elementsPage]);

  // A write made elsewhere on the page: read the open rows again.
  const firstRefresh = useRef(true);
  useEffect(() => {
    if (firstRefresh.current) { firstRefresh.current = false; return; }
    refreshLoaded().catch(() => {});
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = useCallback(async (row, status) => {
    if (!canEdit) return;
    setBusy(row.key);
    try {
      const r = await setElementStatus(projectId, { uniqueIds: [row.uniqueId], status, date: dateForStatus(status, statusDate, projectedDate) });
      setRows((prev) => {
        const walk = (list) => list.map((x) => (x.key === row.key ? { ...x, status } : (Array.isArray(x.children) ? { ...x, children: walk(x.children) } : x)));
        return walk(prev);
      });
      message.success(`${r.count} element · ${STATUS_LABEL[status]}`);
      if (onStatusChanged) onStatusChanged([row.uniqueId], status);
      // The group's counts moved, and under a status filter the row itself
      // may no longer match.
      refreshLoaded().catch(() => {});
    } catch (e) {
      if (!e.cancelled) message.error(e.message || "Could not set the status");
    } finally {
      setBusy("");
    }
  }, [canEdit, projectId, statusDate, projectedDate, onStatusChanged, refreshLoaded]);

  // A status on a whole category or group (owner, 2026-09-18: per tree
  // level, down to every element under it). The count is previewed and
  // confirmed first (root-8e's statusBulk), a move back needs its own yes,
  // and the toast offers Undo. The model recolours from the ids returned.
  const setNodeStatus = useCallback(async (row, status) => {
    if (!canEdit) return;
    setBusy(row.key);
    try {
      const node = row.kind === "category" ? { category: row.category }
        : (row.sized ? { category: row.category, material: row.material, size: row.size } : { category: row.category, group: row.group });
      // A row's status on a page set to levels marks that row ON those levels,
      // and inside a filter or set only the elements the row shows.
      const r = await setStatusWithPreview(projectId, {
        // A shortcut row is the nodes under it (for Conduits by size, the
        // whole Conduits category).
        ...(row.kind === "shortcut" && row.uniqueIds ? { uniqueIds: row.uniqueIds } : { recordNodes: row.kind === "shortcut" ? row.recordNodes : [node] }),
        status, documentIds: docs, date: dateForStatus(status, statusDate, projectedDate),
        ...(pageLevels.length ? { pageLevels } : {}), ...(cutUids ? { cutUids } : {}), ...(serverFilters ? { filters: serverFilters } : {}),
      });
      if (!r) return;
      announceStatus(projectId, r, status, () => { if (onStatusChanged) onStatusChanged(null, status); refreshLoaded().catch(() => {}); });
      if (onStatusChanged) onStatusChanged(Array.isArray(r.uniqueIds) ? r.uniqueIds : null, status);
      await refreshLoaded();
    } catch (e) {
      if (!e.cancelled) message.error(e.message || "Could not set the status");
    } finally {
      setBusy("");
    }
  }, [canEdit, projectId, docs, statusDate, projectedDate, onStatusChanged, refreshLoaded, pageLevelsKey, cutUids, serverFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const rebuild = async () => {
    try {
      const r = await rebuildQuantities(projectId);
      setQuantities(r.quantities || null);
      message.info("Reading the quantities again from the model files");
    } catch (e) {
      message.error(e.message || "Could not start");
    }
  };

  // Filter what is loaded, keeping the ancestors of a match.
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    const walk = (list) => list.map((r) => {
      if (r.kind === "loading" || r.kind === "more") return null;
      const kids = Array.isArray(r.children) ? walk(r.children).filter(Boolean) : undefined;
      const hit = String(r.label || "").toLowerCase().includes(q) || String(r.type || "").toLowerCase().includes(q);
      if (hit) return r;
      if (kids && kids.length) return { ...r, children: kids };
      return null;
    }).filter(Boolean);
    return walk(rows);
  }, [rows, filter]);

  // HOW CERTAIN EACH NUMBER IS, said where the number is (owner, 2026-09-29).
  // "~" is an estimate the exporter derived from a bounding box - an upper
  // bound, not a quantity - and "?" is a model exported before the plugin
  // recorded that at all. A reported value carries no mark, so the marks only
  // ever appear where something is genuinely less than certain.
  //
  // ELEMENT ROWS ONLY. A roll-up mixes reported and estimated children and a
  // single mark on a total would say something untrue about all of them; the
  // banner above the table carries the count instead.
  const flagAware = !!(quantities && quantities.flagAware);
  const numCell = (col, row) => {
    if (row.kind === "loading" || row.kind === "more") return null;
    const src = row.kind === "element" ? row.measures : row.sums;
    if (!src) return null;
    const v = col.key === "count" && row.kind !== "element" ? (row.sums && row.sums.count) || row.n : src[col.key];
    const unit = unitOf(col, units);
    const cert = row.kind === "element" && v ? certaintyOf(row.measures, col.key, flagAware) : CERTAINTY.REPORTED;
    const mark = certaintyMark(cert);
    const title = mark ? `${exact(v, unit)} - ${certaintyTitle(cert, col.title || col.key)}` : exact(v, unit);
    return (
      <Tooltip getPopupContainer={popupInStage} title={title}>
        <span style={{ fontVariantNumeric: "tabular-nums", color: v ? T.ink : "#C0C4C9" }}>
          {mark ? <span style={{ color: "#B45309", marginRight: 1 }}>{mark}</span> : null}
          {compact(v)}
        </span>
      </Tooltip>
    );
  };

  // % installed at EVERY level, with the denominator SAID (owner via the
  // leader, 2026-09-20). A row that carries run length is measured in
  // length - that is what the field installs - and a row without one is
  // counted. A row holding both says BOTH: one blended number would mean
  // neither. Everything here comes from what groupLevel already returns.
  const shareOf = (row) => {
    const n = row.n || 0;
    const st = row.status || {};
    const lengthTotal = (row.sums && row.sums.length) || 0;
    const installedN = st.installed ? st.installed.n : 0;
    const installedLen = st.installed ? (st.installed.length || 0) : 0;
    const beingN = st.being_installed ? st.being_installed.n : 0;
    const beingLen = st.being_installed ? (st.being_installed.length || 0) : 0;
    return {
      n,
      lengthTotal,
      byCount: { pct: n ? Math.round((installedN / n) * 1000) / 10 : 0, installed: installedN, being: beingN, left: Math.max(0, n - installedN) },
      byLength: lengthTotal
        ? { pct: Math.round((installedLen / lengthTotal) * 1000) / 10, installed: installedLen, being: beingLen, left: Math.max(0, lengthTotal - installedLen) }
        : null,
    };
  };
  const shareBar = (row, { compact = false } = {}) => {
    // A SPLIT NOBODY WORKED OUT IS NOT 0% INSTALLED. An empty status object
    // would otherwise draw a full-width 0% bar, which reads as "none of this
    // is done" rather than "not counted" - so the row says so instead.
    if (row.statusUnknown) {
      return (
        <Tooltip getPopupContainer={popupInStage} title="The installed share of these parts was not worked out for this row.">
          <span style={{ fontSize: 11, color: T.muted }}>not counted</span>
        </Tooltip>
      );
    }
    const sh = shareOf(row);
    const main = sh.byLength || sh.byCount;
    const unit = sh.byLength ? units.length : null;
    // A CUSTOM CATEGORY COUNTS PARTS, NOT ELEMENTS, and the fallback wording
    // on this bar is the only place that says the noun out loud. Calling
    // 30,240 beam clamps "elements" on the row directly under the elements
    // they sit inside is the kind of small wrong word that makes a number
    // look like a different number.
    const noun = (row.kind === "derived" || row.kind === "derivedPart") ? "part" : "element";
    const amount = (v) => (unit ? exact(v, unit) : `${Math.round(v).toLocaleString()} ${noun}${Math.round(v) === 1 ? "" : "s"}`);
    const tip = (
      <span style={{ whiteSpace: "pre-line" }}>
        {`${main.pct}% installed of ${amount(sh.byLength ? sh.lengthTotal : sh.n)}\n`}
        {`installed ${amount(main.installed)} · being installed ${amount(main.being)} · not installed ${amount(main.left)}`}
        {sh.byLength ? `\nand ${sh.byCount.pct}% of ${sh.n.toLocaleString()} ${noun}s${noun === "element" ? " (fittings and equipment carry no length)" : ""}` : ""}
      </span>
    );
    return (
      <Tooltip getPopupContainer={popupInStage} title={tip}>
        <div style={{ minWidth: compact ? 120 : 96, flex: 1 }}>
          <Progress percent={main.pct} size="small" strokeColor={STATUS_COLOR.installed} format={(pp) => `${pp}%`} />
          <div style={{ fontSize: 10, color: T.muted, lineHeight: 1.2, marginTop: -2 }}>
            of {amount(sh.byLength ? sh.lengthTotal : sh.n)}
            {sh.byLength ? ` · ${sh.byCount.pct}% of ${sh.n.toLocaleString()} ${noun === "part" ? "parts" : "el."}` : ""}
          </div>
        </div>
      </Tooltip>
    );
  };

  const statusCell = (row) => {
    if (row.kind === "element") {
      const s = (statusByUid && statusByUid.get(row.uniqueId)) || row.status || "projected";
      const menu = {
        items: STATUS_ORDER.map((k) => ({ key: k, label: <span style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[k] }} />{STATUS_LABEL[k]}</span> })),
        onClick: ({ key }) => setStatus(row, key),
      };
      const label = (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: canEdit ? "pointer" : "default" }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[s] }} />
          <span style={{ fontSize: 12 }}>{STATUS_LABEL[s] || s}</span>
          {canEdit && <DownOutlined style={{ fontSize: 9, color: T.muted }} />}
        </span>
      );
      return canEdit ? <Dropdown getPopupContainer={popupInStage} menu={menu} trigger={["click"]} disabled={busy === row.key}>{label}</Dropdown> : label;
    }
    // A CUSTOM CATEGORY GETS THE BAR BUT NOT THE MENU (owner, 2026-10-05:
    // "add the status bar on those rows").
    //
    // The bar is a reading and it is honest here: the share comes from the
    // parts' own statuses where they have them and their parent element's
    // where they do not, which is the rule these numbers follow everywhere.
    //
    // The MENU is a write, and "set all N under this category" would go out
    // as a record key - which resolves against the record tree, where a
    // custom category has no row. It would report a count and change
    // nothing. Status is set on the elements, or on the parts themselves;
    // not from here.
    if (row.kind === "derived" || row.kind === "derivedPart") {
      const bar = shareBar(row);
      const from = row.fromParts
        ? `${row.fromParts.toLocaleString()} of these parts carry their own status; the rest follow the element they are inside.`
        : "These parts follow the status of the element they are inside.";
      return (
        <Tooltip getPopupContainer={popupInStage} title={from}>
          <span style={{ display: "inline-block", width: "100%" }}>{bar}</span>
        </Tooltip>
      );
    }
    if (row.kind !== "category" && row.kind !== "group" && row.kind !== "shortcut") return null;
    const total = row.n || 0;
    const bar = shareBar(row);
    if (!canEdit) return bar;
    const menu = {
      items: [
        { key: "_t", type: "group", label: `Set all ${total.toLocaleString()} under this ${row.kind === "shortcut" ? "entry" : row.kind}` },
        ...STATUS_ORDER.map((k) => ({ key: k, label: <span style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[k] }} />{STATUS_LABEL[k]}</span> })),
      ],
      onClick: ({ key }) => setNodeStatus(row, key),
    };
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {bar}
        <Dropdown getPopupContainer={popupInStage} menu={menu} trigger={["click"]} disabled={busy === row.key}>
          <Button size="small" type="text" loading={busy === row.key} style={{ padding: "0 4px" }} aria-label={`Set the status of everything under ${row.label}`}>
            <DownOutlined style={{ fontSize: 10 }} />
          </Button>
        </Dropdown>
      </div>
    );
  };

  // A TICK MEANS THE WHOLE BRANCH, including the rows that have not been
  // loaded (owner via the leader, 2026-09-20: parent selection must affect
  // the branch). Only the ticked node is remembered - the page resolves it
  // on the server (recordKeys -> POST quantities/uids), because below a
  // collapsed row are placeholders, so walking the tree here would answer
  // "nothing".
  //
  // A row under a ticked category shows ticked and is not clickable: its
  // category really does include it, and letting it be unticked would
  // silently drop the siblings nobody has loaded. Untick the category to
  // pick inside it - said in the tooltip, not left to be discovered.
  const checkedSet = useMemo(() => new Set((checked || []).map(String)), [checked]);
  const excludedSet = useMemo(() => new Set((excluded || []).map(String)), [excluded]);
  const tickCell = (row) => {
    if (!onChecked || row.kind === "loading" || row.kind === "more") return null;
    // A CUSTOM CATEGORY NOW HAS A TICK BOX HERE (owner, 2026-10-05: "these 3
    // categories still don't have a checkbox inside the tracking quantity
    // table... they need to behave exactly like normal categories, when it
    // comes to operations and printing, and status").
    //
    // HE IS OVERRULING ME FOR THE SECOND TIME ON THIS and my objection is on
    // the record: a tick here used to become a record key, which the server
    // turns into a match on ElementQuantity's record tree, and a custom
    // category has no record row - so the box would tick and the write would
    // land on nothing. That is still true of a record key, which is why the
    // tick does NOT travel as one: parseRecordKey learned a `derived:<id>`
    // kind and nodesForKeys hands those out as derivedIds, which the server
    // resolves to the PART ids. The objection was about the key format, and
    // the key format changed.
    //
    // A PART GROUP INSIDE ONE still has no box. Ticking "the 3/8in nuts of
    // this category" would need a definition that does not exist, and
    // inventing one from a tick is how a saved selection stops matching what
    // it was saved from. The whole category is the unit he asked for.
    if (row.kind === "derivedPart") {
      return (
        <Tooltip
          getPopupContainer={popupInStage}
          title="Tick the custom category itself - a single part group inside it is not a category the model can resolve."
        >
          <span style={{ color: "#C0C4C9", fontSize: 11 }}>—</span>
        </Tooltip>
      );
    }
    // A SHORTCUT WITHOUT ONE REAL KEY (Junction boxes, Pull boxes) is its
    // groups: ticked when all of them are, and a click ticks or unticks
    // each of them exactly as clicking it in Electrical Fixtures would.
    if (row.kind === "shortcut" && !row.realKey) {
      const kids = row.childRows || (row.childKeys || []).map((k) => ({ kind: "group", key: k, category: row.category }));
      const onOf = (k) => checkedBy(k, checkedSet).checked && !excludedBy(k, excludedSet);
      const all = kids.length > 0 && kids.every(onOf);
      const some = kids.some(onOf);
      return (
        <Checkbox
          checked={all} indeterminate={!all && some}
          onChange={(e) => {
            let nextChecked = checked; let nextExcluded = excluded;
            for (const k of kids) {
              const st = checkedBy(k, new Set((nextChecked || []).map(String)));
              const ex = excludedBy(k, new Set((nextExcluded || []).map(String)));
              if (e.target.checked === (st.checked && !ex)) continue;
              if (st.byAncestor) { nextExcluded = toggleExclusion(nextExcluded, k, e.target.checked); continue; }
              if (ex) nextExcluded = toggleExclusion(nextExcluded, k, true);
              nextChecked = toggleRecordKey(nextChecked, k, e.target.checked);
            }
            if (nextExcluded !== excluded && onExcluded) onExcluded(nextExcluded);
            if (nextChecked !== checked) onChecked(nextChecked);
          }}
          aria-label={`Select ${row.label}`}
        />
      );
    }
    // Any other row of a shortcut is its real node: same key, same tick.
    row = row.realKey ? { ...row, key: row.realKey, kind: row.kind === "shortcut" ? "category" : row.kind } : row;
    const state = checkedBy(row, checkedSet);
    const out = excludedBy(row, excludedSet);
    // A row under a ticked parent can now be unticked on its own: the
    // parent stays ticked and this row becomes an EXCLUSION. It used to be
    // filled and disabled, because unticking it would have silently
    // dropped the rows nobody had loaded - the exclusion is the honest
    // version of the same wish.
    const on = state.checked && !out;
    const box = (
      <Checkbox
        checked={on}
        onChange={(e) => {
          if (state.byAncestor) { if (onExcluded) onExcluded(toggleExclusion(excluded, row, e.target.checked)); return; }
          if (out && onExcluded) onExcluded(toggleExclusion(excluded, row, true));
          onChecked(toggleRecordKey(checked, row, e.target.checked));
        }}
        aria-label={`Select ${row.label}`}
      />
    );
    if (out) {
      const by = String(out).split(":").slice(1).join(":").split("|").pop();
      return <Tooltip getPopupContainer={popupInStage} title={out === row.key ? "Left out of the selection - tick it to put it back" : `Left out with "${by}"`}><span>{box}</span></Tooltip>;
    }
    if (!state.byAncestor) return box;
    const above = String(state.byAncestor).split(":").slice(1).join(":").split("|").pop();
    return <Tooltip getPopupContainer={popupInStage} title={`Included because "${above}" is ticked - untick it to leave just this one out`}><span>{box}</span></Tooltip>;
  };

  // The rows this tree can honestly say are in the selection made
  // elsewhere. A Set of uids and a Set of levels, both cheap to test per
  // row, and both empty when nothing is selected elsewhere.
  const litUids = useMemo(() => (selectedUids && selectedUids.size ? selectedUids : null), [selectedUids]);
  const litLevels = useMemo(() => new Set(selectedLevels || []), [selectedLevels]);
  const isLit = useCallback((row) => {
    if (!row) return false;
    if (row.kind === "element") return !!(litUids && litUids.has(row.uniqueId)) || (!!litLevels.size && litLevels.has(row.level || "(no level)"));
    // Category rows carry their levels too, from 2026-09-20: the roll-up
    // had always stored them per group row and the category listing
    // simply never asked. So the same test serves both parents, and a
    // category with no level recorded under it stays unlit - a real
    // boundary rather than a gap in the marking.
    if ((row.kind === "group" || row.kind === "category" || row.kind === "shortcut") && litLevels.size) {
      return (row.levels || []).some((l) => litLevels.has(l));
    }
    return false;
  }, [litUids, litLevels]);
  const litCount = useMemo(() => (litUids ? litUids.size : 0), [litUids]);

  const columns = [
    ...(onChecked ? [{
      // ON TOP OF ALL THE CHECKBOXES, BEFORE THE HEADER NAME. It selects
      // what the FILTER matched - resolved by the server, never gathered
      // from the rows on screen, because this tree is lazy and a
      // collapsed branch is not in the browser at all. A selection
      // gathered from what is rendered would be silently short, and the
      // number it produced is the number a status gets written onto.
      //
      // It is offered only while a filter is ON. Without one it would
      // mean "every element in the tree", which on this project is a
      // one-click status write over a million rows - a different feature,
      // and not one anybody asked for.
      title: onAllFiltered ? (
        <Tooltip getPopupContainer={popupInStage} title={filtering
          ? (allFilteredCount === null ? "Select everything this filter matched" : `Select all ${Number(allFilteredCount).toLocaleString()} elements this filter matched`)
          : "Filter a column first - this selects everything the filter matched"}>
          <span>
            <Checkbox
              checked={allFiltered}
              disabled={!filtering}
              onChange={(e) => onAllFiltered(e.target.checked)}
            />
          </span>
        </Tooltip>
      ) : "",
      key: "tick", fixed: "left", width: 40,
      render: (_, row) => tickCell(row),
    }] : []),
    {
      title: "Name", dataIndex: "label", key: "label", fixed: "left", width: 320, ellipsis: true,
      ...textFilter(headerFilters.name, (v) => setOne({ name: v })),
      render: (_, row) => {
        if (row.kind === "loading") return <Spin size="small" />;
        if (row.kind === "more") {
          return <Button size="small" type="link" onClick={() => loadElements(row.group, row.skip, row.have)}>Load {Math.min(PAGE, row.total - row.skip).toLocaleString()} more of {row.total.toLocaleString()}</Button>;
        }
        if (row.kind === "element") {
          // BOTH NAMES, WHERE THEY DIFFER (owner, 2026-10-02: "Show both
          // names on the row"). A ticked category is matched against the
          // model's taxonomy as well as the record one, so ticking
          // "Supports" finds its 150 stair stringers - and they appear
          // under Stairs, because that is where the record data files
          // them. This says so, per row, from the row's own data.
          const modelName = modelNameFor(row, row.recordCategory);
          return (
            <span>
              <Tooltip getPopupContainer={popupInStage} title={`${row.label}${row.matched ? "" : " - not in the published model"} · ${row.uniqueId}`}>
                <a onClick={() => onSelectElement && row.matched && onSelectElement(row.uniqueId)} style={{ color: row.matched ? undefined : T.muted }}>{row.label}</a>
              </Tooltip>
              {modelName ? (
                <Tooltip getPopupContainer={popupInStage} title={modelNameTooltip(row, row.recordCategory)}>
                  <Tag style={{ marginLeft: 6, fontSize: 10.5, lineHeight: "15px", padding: "0 4px" }}>{modelName}</Tag>
                </Tooltip>
              ) : null}
            </span>
          );
        }
        const dropped = !!excludedBy(row.realKey ? { ...row, key: row.realKey, kind: row.kind === "shortcut" ? "category" : row.kind } : row, excludedSet);
        return (
          <span style={dropped ? { opacity: 0.45, textDecoration: "line-through" } : undefined}>
            <b style={{ fontWeight: row.kind === "category" || row.kind === "shortcut" ? 700 : 600 }}>{row.label}</b>
            {onIsolate && canIsolateRow(row) ? (() => {
              const keys = isolateKeysFor(row);
              const on = !!isolatedStamp && isolatedStamp === keys.join("~~");
              return (
                <Tooltip getPopupContainer={popupInStage} title={on
                  ? "Put the model back and clear this selection"
                  : `Select every ${row.label} element and show only them - then Set status applies to exactly these`}>
                  <Button
                    size="small" type="link" style={{ padding: "0 4px", height: 18, fontSize: 12 }}
                    data-testid={`isolate-${row.label}`}
                    onClick={(e) => { e.stopPropagation(); onIsolate(keys); }}
                  >
                    {on ? "Show all" : "Isolate"}
                  </Button>
                </Tooltip>
              );
            })() : null}
            {row.kind === "group" && row.type ? <span style={{ color: T.muted, marginLeft: 6, fontSize: 12 }}>{row.type}</span> : null}
            {row.sourceCategory ? <span style={{ color: T.muted, marginLeft: 6, fontSize: 12 }}>{`from ${row.sourceCategory}`}</span> : null}
            {/* A ROW THAT COULD NOT BE RESOLVED IS NOT A ROW OF ZERO.
                (row.n || 0) would print "(0)", which reads as "this
                category has nothing in it under your filter" - the
                opposite of "it has too much to work out". */}
            {/* A ZERO THAT CAN BE ACCOUNTED FOR. "(0)" with a row of dashes
                looks the same whether the filter excluded everything or the
                category is genuinely empty - and the owner could tell
                neither (2026-10-05). The server says which. */}
            {row.filteredToNothing ? (
              <span style={{ color: T.muted, marginLeft: 6, fontSize: 12 }}>
                {"- none of these parts' parent elements are in the current filter, so there is nothing to count. Clear the filter above to see them."}
              </span>
            ) : null}
            {row.tooManyParents ? (
              <span style={{ color: T.muted, marginLeft: 6, fontSize: 12 }}>
                {`- ${row.tooManyParents.toLocaleString()} parent elements is more than this can filter at once (${(row.parentLimit || 0).toLocaleString()}). Narrow the part type and make a second category.`}
              </span>
            ) : (
              <span style={{ color: T.muted, marginLeft: 6, fontSize: 12 }}>({(row.n || 0).toLocaleString()})</span>
            )}
          </span>
        );
      },
    },
    {
      title: "Level", key: "level", width: 130, ellipsis: true,
      ...pickFilter(levelChoices.map((l) => ({ value: l, label: l })), headerFilters.levels, (v) => setOne({ levels: v })),
      // A CUSTOM CATEGORY SHOWS THE LEVELS ITS PARTS SIT ON (owner,
      // 2026-10-05: "do the level filter on parts too"). A part carries no
      // level of its own - it is where its parent is - so these are the
      // parents' levels, read from the same place the Level filter
      // resolves against. The column and the filter cannot disagree.
      render: (_, row) => (row.kind === "element" ? row.level
        : (row.kind === "group" || row.kind === "shortcut" || row.kind === "derived" || row.kind === "derivedPart")
          ? (row.levels || []).join(", ") : ""),
    },
    ...TABLE_SUMS.map((col) => ({
      title: <span>{col.label}{col.unit ? <span style={{ color: T.muted, fontWeight: 400 }}> {unitOf(col, units)}</span> : null}</span>,
      key: col.key, width: col.key === "surfaceArea" ? 110 : 90, align: "right",
      ...rangeFilter(unitOf(col, units), headerFilters.ranges[col.key], (r) => setRange(col.key, r)),
      render: (_, row) => numCell(col, row),
    })),
    ...DETAIL_DIMENSIONS.map((col) => ({
      title: <span>{col.label}<span style={{ color: T.muted, fontWeight: 400 }}> {unitOf(col, units)}</span></span>,
      key: col.key, width: 90, align: "right",
      ...rangeFilter(unitOf(col, units), headerFilters.ranges[col.key], (r) => setRange(col.key, r)),
      render: (_, row) => (row.kind === "element" ? numCell(col, row) : null),
    })),
    {
      title: "Status", key: "status", width: 170, fixed: "right",
      // "Projected" is the ABSENCE of a status row rather than a value, and
      // the menu offers it like any other because that is how it reads on
      // screen; the server turns it into the right question.
      ...pickFilter(STATUS_ORDER.map((k) => ({ value: k, label: STATUS_LABEL[k] })),
        headerFilters.statuses, (v) => setOne({ statuses: v })),
      render: (_, row) => statusCell(row),
    },
  ];

  // The roll-up at the top: the whole tree, from its top-level rows. Same
  // two denominators, never one blended number.
  const overall = useMemo(() => {
    const cats = (rows || []).filter((r) => r.kind === "category");
    if (!cats.length) return null;
    const add = (a, b) => ({ n: a.n + (b.n || 0), length: a.length + ((b.sums && b.sums.length) || 0) });
    const totals = cats.reduce((a, r) => add(a, r), { n: 0, length: 0 });
    const statusOf = (k) => cats.reduce((a, r) => {
      const x = (r.status || {})[k];
      return { n: a.n + ((x && x.n) || 0), length: a.length + ((x && x.length) || 0) };
    }, { n: 0, length: 0 });
    return {
      kind: "category",
      label: "Everything loaded",
      n: totals.n,
      sums: { length: totals.length },
      status: { installed: statusOf("installed"), being_installed: statusOf("being_installed"), procured: statusOf("procured") },
    };
  }, [rows]);

  const note = buildNote(quantities);
  // ...and how much of what IS built can be trusted as a measurement.
  const certNote = certaintyNote(quantities);
  return (
    <div>
      <Space wrap style={{ marginBottom: 8, width: "100%", justifyContent: "space-between" }}>
        <Input size="small" allowClear prefix={<SearchOutlined style={{ color: T.muted }} />} placeholder="Filter what is loaded"
          value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 280 }} />
        <Space>
          {quantities && quantities.state === "done" && (
            <span style={{ fontSize: 12, color: T.muted }}>
              {scoped ? <Tooltip getPopupContainer={popupInStage} title="Only what the tracking scope selects (Model elements tab, Tracked). An empty scope shows the whole model."><b style={{ color: "#23343E" }}>Tracking scope ({scoped} selection{scoped === 1 ? "" : "s"}) · </b></Tooltip> : "Whole model (no tracking scope saved) · "}
              {(quantities.components || 0).toLocaleString()} elements from {quantities.files} record file{quantities.files === 1 ? "" : "s"}
              {quantities.unmatched ? ` · ${quantities.unmatched.toLocaleString()} not in the published model` : ""}
            </span>
          )}
          <Button size="small" type={(headerFilters.rules || []).length ? "primary" : "default"} ghost={!!(headerFilters.rules || []).length}
            onClick={() => setRulesOpen((o) => !o)}>
            Rules{(headerFilters.rules || []).length ? ` · ${rulesInWords(headerFilters)}` : ""}
          </Button>
          {/* WORKING, said where the filter is set (owner, 2026-09-22): the
              table below shows the new filter's numbers only once this is
              gone - until then it is dimmed, never read as current. */}
          {loading && rows.length ? (
            <span role="status" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: T.muted }}>
              <Spin size="small" /> {filterStamp ? "Filtering…" : "Loading…"}
            </span>
          ) : null}
          {canEdit && (
            <Tooltip getPopupContainer={popupInStage} title="Read the quantities again from the model's record files">
              <Button size="small" icon={<ReloadOutlined />} onClick={rebuild}
                disabled={quantities && (quantities.state === "running" || quantities.state === "queued")}>Rebuild</Button>
            </Tooltip>
          )}
        </Space>
      </Space>
      {/* THE BAR SHOWS FOR A SELECT-ALL TOO. It used to appear only when
          rows were ticked one by one, so after "select everything this
          filter matched" there was NO Set status button in this tab: the
          chain the owner asked for - filter, select what matched, set the
          status - stopped one step short, on the step that writes. Found
          by driving the real panel on staging, not by any test. */}
      {bulkBar && (checked.length || allFiltered) ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", marginBottom: 8, border: `1px solid ${T.line}`, borderRadius: 6, background: "#F6F9FF" }}>
          {bulkBar}
        </div>
      ) : null}
      {overall && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 10px", marginBottom: 8, border: `1px solid ${T.line}`, borderRadius: 6, background: "#FAFBFC" }}>
          <b style={{ fontSize: 12, whiteSpace: "nowrap" }}>All {overall.n.toLocaleString()} elements</b>
          <div style={{ flex: 1, maxWidth: 420 }}>{shareBar(overall, { compact: true })}</div>
          <span style={{ fontSize: 11, color: T.muted }}>installed, of what is loaded in this tree</span>
        </div>
      )}
      {rulesOpen ? (
        <RuleBuilder
          projectId={projectId}
          documentIds={docs}
          listId={listId}
          rules={headerFilters.rules || []}
          gate={headerFilters.gate || "AND"}
          units={units}
          onApply={(rules, gate) => setHeaderFilters((prev) => ({ ...prev, rules, gate }))}
          running={loading}
          canSave={canSaveRules}
        />
      ) : null}
      {/* A FILTER CHANGES WHAT THE NUMBERS MEAN, so it says so. Every count
          and every amount below is of the MATCHING elements only - a
          category's total is not its total while this is on, and somebody
          reading quantities off this screen for real work has to be told
          rather than left to notice. */}
      {filtering && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", marginBottom: 8, border: `1px solid ${T.line}`, borderRadius: 6, background: "#FFF7E6" }}>
          <b style={{ fontSize: 12 }}>
            {[listId ? `Set: ${listName || "chosen set"}` : "", pageLevels.length ? `Page levels: ${pageLevels.join(", ")}` : ""].filter(Boolean).join(" · ") || "Filtered"}
          </b>
          <span style={{ fontSize: 12, color: T.muted, flex: 1 }}>
            {!listId && !anyActive(headerFilters) && pageLevels.length
              ? "only elements on this page's levels are listed - every count and amount below is of them"
              : listId
              ? `only this set's elements are listed${anyActive(headerFilters) ? ", and of those only the ones matching the filter" : ""} - every count and amount below is of them`
              : "every count and amount below is of the matching elements only, at every level of the tree"}
            {(headerFilters.rules || []).length ? ` · ${rulesInWords(headerFilters)}` : ""}
          </span>
          {allFiltered && allFilteredCount !== null ? (
            <b style={{ fontSize: 12 }}>{Number(allFilteredCount).toLocaleString()} selected</b>
          ) : null}
          {anyActive(headerFilters) ? <Button size="small" onClick={() => setHeaderFilters(NO_FILTERS)}>Clear filters</Button> : null}
        </div>
      )}
      {(litCount > 0 || litLevels.size > 0) && (
        <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>
          <span style={{ display: "inline-block", width: 10, height: 10, background: "#E6F4FF", border: "1px solid #91CAFF", borderRadius: 2, marginRight: 6, verticalAlign: "-1px" }} />
          {litLevels.size
            ? `rows on ${[...litLevels].join(", ")} are selected in the details panel`
            : `${litCount.toLocaleString()} element${litCount === 1 ? "" : "s"} selected elsewhere are marked where this tree shows them`}
        </div>
      )}
      <style>{".bl-lit-row > td { background: #E6F4FF !important; }"}</style>
      {note && <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>{note}</div>}
      {/* WHAT THE NUMBERS BELOW ARE WORTH. Amber rather than red: nothing is
          broken and nothing is wrong - these are the exporter's own best
          figures - but an estimated area or volume is an upper bound and a
          person totalling them deserves to know before they do. */}
      {certNote && (
        <div style={{
          fontSize: 12, color: "#7C4A03", background: "#FFFBEB", border: "1px solid #FDE68A",
          borderRadius: 6, padding: "6px 10px", marginBottom: 8,
        }}>
          {certNote}
        </div>
      )}
      {loading && !rows.length ? (
        <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>
      ) : !rows.length ? (
        <Empty description={note || "No quantities for this model yet"} style={{ padding: 24 }} />
      ) : (
        <Table getPopupContainer={popupInStage}
          size="small"
          rowKey="key"
          columns={columns}
          dataSource={shown}
          // Dimmed under a spinner while a filter's answer is on its way:
          // the rows under it belong to the previous filter.
          loading={{ spinning: loading, tip: filterStamp ? "Filtering…" : "Loading…" }}
          pagination={false}
          sticky
          scroll={{ x: onChecked ? 1690 : 1650, y: height }}
          expandable={{ expandedRowKeys: expanded, onExpandedRowsChange: setExpanded, onExpand, indentSize: 18 }}
          rowClassName={(row) => (isLit(row) ? "bl-lit-row" : "")}
        />
      )}
    </div>
  );
}
